/**
 * WHAT THE READ-ONLY PANEL SAYS ABOUT EACH KIND OF RECORD. (ZEN-702)
 *
 * ONE FILE ON PURPOSE. Every label a family reads on the panel lives here, so
 * the wording can be read, reviewed and changed in one place rather than on
 * nine screens. Wherever the editing form already has a label for a field,
 * that label is reused verbatim — the same fact is named the same way whether
 * you are reading it or changing it. The handful of lines that have no form
 * label (a status, a rate the app found, a total) are marked NEW below so they
 * can be found.
 *
 * THESE FUNCTIONS READ WHAT THE SCREEN ALREADY HOLDS AND NOTHING ELSE. They
 * take the record plus the lookup lists the page has already loaded (parties,
 * payment accounts, beneficiaries) and return [{label, value}]. No request is
 * made here, and none may be added: the panel's whole safety argument is that
 * it cannot show a field the account was not already given. A field the page
 * does not hold is simply not on the panel — see the notes on the family
 * envelope (expense) and the 529 account (distribution) below.
 *
 * A value of null/undefined/"" means "do not draw this line". The panel
 * (components/RecordView.jsx) drops those, and drops any heading left with
 * nothing under it.
 */
import { DRAW_QUESTION, holdsReservation } from "@/lib/aotcThirdAnswer";
import {
  money, fmtDate, STATUS_META, closedReasonLabel, EXPENSE_GROUPS,
  DISTRIBUTION_TYPE_LABEL, HOUSING_SITUATION_LABEL, ACCOUNT_TYPE_LABEL,
  UNKNOWN_ACCOUNT_TYPE, UNRECOGNISED_WORD, nameOf, stateName,
} from "@/lib/format";
import { nickFor, expenseRoutingLabel } from "@/lib/parties";
import { psLabel, psById, psOwnerName, fundLast4 } from "@/lib/payments";
import { withdrawalWhen } from "@/lib/withdrawalNames";
import { isUSD, curCode, sameCurrency } from "@/lib/currencies";
import { isOverpaid } from "@/lib/advanceOwed";
import { academicYearLabel, termMonthsLong, termMonthsShort } from "@/lib/schoolTerms";

// A narrow screen must not split "Sep–Dec 2026" after the dash: non-breaking
// spaces, and an invisible word joiner after the dash. It reads the same.
const keepTogether = (t) => t.replace(/ /g, "\u00a0").replace(/\u2013/g, "\u2013\u2060");

/** The panel's title for each kind of record. */
export const RECORD_TITLES = {
  expense: "Expense details",
  withdrawal: "529 distribution details",
  advance: "Advance details",
  envelope: "Contribution details",
  gift: "Gift details",
  beneficiary: "Beneficiary details",
  account: "Account details",
  payer: "Payer details",
  plan: "529 account details",
  scholarship: "Scholarship details",
  settlement: "Settlement details",
  repayment: "Repayment details",
};

const yesNo = (b) => (b ? "Yes" : "No");
// The answer as the expense window shows it: an expense holding a set-aside
// reads "Partly", the third answer (ZEN-833, approved 31 Oct 2026).
const answerToThe529 = (e) => (holdsReservation(e) ? "Partly" : yesNo(e.draw_from_529));
// The pages' name lookups answer "—" while their beneficiary list is still
// loading or when the id is unknown. That is a placeholder, not a name, and
// the panel would otherwise draw "Beneficiary —": the exact line this whole
// component exists not to draw. (Adversarial review, round 1.)
const nameOrNothing = (v) => (typeof v === "string" && v.trim() && v.trim() !== "—" ? v : null);
const yesOrNothing = (b) => (b ? "Yes" : null);
const num = (v) => (v === null || v === undefined || v === "" ? null : Number(v));
const groupLabel = (key) => EXPENSE_GROUPS.find((g) => g.key === (key || "post_secondary"))?.label || null;

// The words the rest of the app already uses for where a rate came from
// (Advances card, expense form). One copy for the panel.
// A typed rate is never attributed to a provider, whatever the row stores: old
// rows kept the previous one when a rate was overtyped, and "ECB" beside "Rate
// entered by hand: Yes" contradicts itself on a tax record. (Review, round 2.)
export const fxSourceName = (source) =>
  source === "manual" ? "Manual" : source === "treasury" ? "US Treasury" : "ECB";

/** "1 CAD = 0.72130 USD · ECB · 12 Jun 2026" — the Advances card's own shape. */
export function fxLine(currency, rate, source, rateDate) {
  const r = num(rate);
  if (!r || isUSD(currency)) return null;
  const parts = [`1 ${curCode(currency)} = ${r.toFixed(5)} USD`, fxSourceName(source)];
  if (rateDate) parts.push(fmtDate(rateDate));
  return parts.join(" · ");
}

const dollars = (v) => { const n = num(v); return n === null ? null : money(n, "USD"); };
// US dollars read as "$1,234.00" everywhere else in the app; only a foreign
// amount carries its code ("CAD 1,234.00").
const inCurrency = (v, cur) => { const n = num(v); if (n === null) return null; return money(n, curCode(cur, "USD")); };

// ── EXPENSE ────────────────────────────────────────────────────────────────
//
// ctx: { parties, psSources, students, student (the beneficiary with schools) }
//
// THE FAMILY ENVELOPE IS NOT HERE, DELIBERATELY. The expense carries only the
// envelope's id, and the Expenses screen does not load the envelope list, so
// naming it would take a request — which this panel may not make. Adding it is
// a different ticket with a different class.
// "US$574.97 from 2 withdrawals: US$274.97 (received 23/Mar/2026), …" —
// wording approved by Maryse, 17 Oct 2026. Dates come from the page's own
// withdrawal list (ctx.withdrawals); nothing is fetched here. (ZEN-785)
function matchedLine(e, withdrawals = []) {
  const raw = Array.isArray(e.allocations) ? e.allocations : [];
  const byWithdrawal = new Map();
  for (const a of raw) {
    if (!a || typeof a !== "object" || !(num(a.amount_usd) > 0.005)) continue;
    byWithdrawal.set(a.withdrawal_id, (byWithdrawal.get(a.withdrawal_id) || 0) + num(a.amount_usd));
  }
  if (!byWithdrawal.size) return null;
  const byId = Object.fromEntries(withdrawals.map((w) => [w.id, w]));
  let total = 0;
  const parts = [...byWithdrawal].map(([wid, amt]) => {
    total += amt;
    const when = withdrawalWhen(byId[wid]);
    return when ? `${money(amt, "USD")} (${when})` : money(amt, "USD");
  });
  const n = byWithdrawal.size;
  return `${money(total, "USD")} from ${n} withdrawal${n === 1 ? "" : "s"}: ${parts.join(", ")}`;
}

export function expenseFields(e, ctx = {}) {
  const { parties, psSources = [], students = [], student, withdrawals = [] } = ctx;
  const ben = students.find((s) => s.id === e.student_id);
  const nick = ben?.nickname || ben?.full_name?.split(" ")[0] || "Beneficiary";
  const schools = student?.schools || [];
  const school = schools.find((s) => s.id === e.school_enrollment_id);
  let period = null;
  for (const s of schools) {
    for (const y of s.coa_by_year || []) {
      const p = (y.periods || []).find((x) => x.id === e.coa_period_id);
      // ZEN-788: the same year heading the terms table shows for this year.
      if (p) period = { ...p, yearLabel: academicYearLabel(y.periods) || y.label || String(y.year || "") };
    }
  }
  const foreign = e.original_currency && !isUSD(e.original_currency);
  const ps = psById(psSources, e.payment_source_id);
  const decided = e.status !== "draft";
  const payorRef = e.payor_ref
    || (e.payor_type === "party" && e.payor_party_id ? `party:${e.payor_party_id}` : e.payor_type);
  const status = nameOf(STATUS_META, e.status, null)?.short || null;
  const closed = closedReasonLabel(e);

  return [
    { label: "Beneficiary", value: ben?.full_name || null },
    { label: "Transaction date", value: e.transaction_date ? fmtDate(e.transaction_date) : null },
    { label: "Payee / Issued by", value: e.payee },
    { label: "Description", value: e.description },
    { label: "Amount", value: inCurrency(e.original_amount, e.original_currency) },
    // NEW — the row shows the US-dollar figure; the panel says what it is
    { label: "US dollar amount", value: foreign ? [dollars(e.usd_amount), e.is_fx_estimate ? "estimate" : null].filter(Boolean).join(" · ") || null : null },
    { label: "Exchange rate", value: foreign ? fxLine(e.original_currency, e.fx_rate, e.fx_override ? "manual" : e.fx_source, e.fx_rate_date) : null },
    { label: "Rate entered by hand", value: foreign ? yesOrNothing(e.fx_override) : null },
    { label: "Category", value: e.category },
    { label: "Expense type", value: groupLabel(e.expense_group) },
    { label: "Status", value: [status, closed].filter(Boolean).join(" · ") || null },
    // The one sentence the row already uses for eligibility + who pays whom.
    { label: "529 handling", value: expenseRoutingLabel(e, parties, nick) },
    { label: DRAW_QUESTION, value: decided && e.eligible_529 ? answerToThe529(e) : null },
    { label: "529 money matched", value: matchedLine(e, withdrawals) },
    { label: "Reimbursed by a party?", value: decided && e.reimbursed_by_ref && e.reimbursed_by_ref !== "none" ? nickFor(e.reimbursed_by_ref, parties, nick) : null },
    { label: "Counts toward tax year", value: e.eligibility_tax_year || null },
    { label: "School enrollment", value: school?.school_name || null },
    { label: "COA period", value: period ? [period.yearLabel, nameOf(HOUSING_SITUATION_LABEL, period.situation, period.situation_custom || UNRECOGNISED_WORD), keepTogether(termMonthsShort(period.from_month, period.to_month))].filter(Boolean).join(" · ") : null },
    { label: "Paid by", value: payorRef ? nickFor(payorRef, parties, nick) : null },
    { label: "Payment Account", value: ps ? psLabel(ps, psOwnerName(ps, students, parties)) : null },
    { label: "American Opportunity Tax Credit", value: e.aotc_reserved ? ["Reserved", dollars(e.aotc_reserved_amount)].filter(Boolean).join(" · ") : null },
    { label: "Notes", value: e.notes },
  ];
}

// ── 529 DISTRIBUTION ───────────────────────────────────────────────────────
//
// ctx: { parties } — OPTIONAL, and the distributions screen passes none: it
// does not load the payer list, and the panel may not add a request. Without
// it, the owner reads as the form's own fallback word and another payer as
// "Another party" (a new sentence, listed for Maryse).
//
// THE 529 ACCOUNT IT CAME FROM IS NOT HERE. The distributions screen does not
// load the plan list (only the editing form does, when it opens), and the
// record carries only the plan's id. Same rule as the envelope above.
export function withdrawalFields(w, ctx = {}) {
  const { parties } = ctx;
  const recipient = (() => {
    switch (w.actual_recipient) {
      case "student": return "Beneficiary";
      case "institution": return "Institution";
      case "owner": return parties?.owner?.nickname || "Owner";
      case "party": return parties ? nickFor(`party:${w.actual_recipient_party_id}`, parties) : "Another party";
      case "other": return w.actual_recipient_other || "Other";
      default: return null;
    }
  })();
  const roth = w.distribution_type === "roth_rollover";
  return [
    { label: "Kind", value: nameOf(DISTRIBUTION_TYPE_LABEL, w.distribution_type, UNRECOGNISED_WORD) },
    { label: "Mode", value: w.mode === "lump_sum" ? "Lump sum (front-loaded)" : w.mode === "reimbursement" ? "Reimbursement (expense-matched)" : null },
    { label: "Date requested", value: w.date_requested ? fmtDate(w.date_requested) : null },
    { label: "Date received", value: w.date_received ? fmtDate(w.date_received) : null },
    { label: "Amount (USD)", value: dollars(w.amount) },
    { label: "Counts toward tax year", value: w.eligibility_tax_year || null },
    { label: "Intended recipient", value: w.intended_recipient === "institution" ? "Institution (direct)" : w.intended_recipient === "student" ? "Beneficiary account" : null },
    { label: "Actual recipient", value: recipient },
    { label: "Actual recipient confirmed", value: yesOrNothing(w.actual_recipient_confirmed) },
    { label: "Actual recipient note", value: w.actual_recipient_note },
    { label: "529 balance before this distribution", value: dollars(w.balance_prior) },
    { label: "Date of 529 balance", value: num(w.balance_prior) !== null && w.balance_prior_date ? fmtDate(w.balance_prior_date) : null },
    { label: "529 balance after", value: num(w.balance_prior) !== null ? dollars(w.balance_after) : null },
    { label: "Loan servicer", value: w.loan_servicer },
    { label: "Roth account note", value: w.roth_account_note },
    { label: "Direct trustee-to-trustee transfer", value: roth ? yesNo(w.trustee_to_trustee !== false) : null },
    // NEW — the card's own "Tied total" figure, in words
    { label: "Tied total", value: num(w.allocated_total) !== null ? `${money(w.allocated_total, "USD")} / ${money(w.amount, "USD")}` : null },
    { label: "Notes", value: w.notes },
  ];
}

// ── MONEY ADVANCE ──────────────────────────────────────────────────────────
//
// ctx: { studentName, students, parties, psSources, bankAccounts }
// NEW (ZEN-770) — "Counts as": a repayment in another currency, in the advance's.
function repaymentCountsAs(r, a) {
  const advCur = curCode(a.currency, "USD");
  const cur = curCode(r.currency || a.repay_currency || a.currency, "USD");
  if (r.unconverted) return "Not yet converted";
  if (sameCurrency(cur, advCur) || num(r.advance_amount) === null) return null;
  const rate = num(r.conv_rate);
  return `${money(num(r.advance_amount), advCur)} at 1 ${cur} = ${rate === null ? "—" : rate.toFixed(5)} ${advCur}`
    + (r.conv_estimate ? ` · estimate until the ${fmtDate(r.date)} rate is published` : "")
    + (r.conv_source === "manual" ? " · rate entered by hand" : "");
}

// One repayment of a cash advance. `labelOf(id)` names an account (ZEN-771).
export function repaymentFields(r, a, labelOf = () => null) {
  return [
    { label: "Date", value: r.date ? fmtDate(r.date) : null },
    { label: "Amount", value: inCurrency(r.amount, r.currency || a.repay_currency || a.currency) },
    { label: "Counts as", value: repaymentCountsAs(r, a) },
    { label: "From account", value: r.from_account ? labelOf(r.from_account) : null },
    { label: "To account", value: r.to_account ? labelOf(r.to_account) : null },
    { label: "Notes", value: r.note },
  ];
}

export function advanceFields(a, ctx = {}) {
  const { studentName, students = [], parties, psSources = [], bankAccounts = [] } = ctx;
  const foreign = a.currency && !isUSD(a.currency);
  const psFrom = psById(psSources, a.payment_source_id);
  const bankTo = bankAccounts.find((b) => b.id === a.to_payment_source_id);
  const anyAccount = (id) => {
    if (!id) return null;
    const b = bankAccounts.find((x) => x.id === id);
    if (b) return b.label || psLabel(b, psOwnerName(b, students, parties)) || null;
    const p = psById(psSources, id);
    return p ? psLabel(p, psOwnerName(p, students, parties)) : null;
  };
  const repayCur = curCode(a.repay_currency || a.currency, "USD");
  return [
    { label: "Beneficiary", value: nameOrNothing(studentName) },
    { label: "From", value: a.from_nickname },
    { label: "Date", value: a.date ? fmtDate(a.date) : null },
    { label: "Amount", value: inCurrency(a.amount, a.currency) },
    { label: "US dollar amount", value: foreign ? dollars(a.usd_amount) : null },
    { label: "Exchange rate", value: foreign ? fxLine(a.currency, a.fx_rate, a.fx_override ? "manual" : a.fx_source, a.fx_rate_date) : null },
    { label: "Rate entered by hand", value: foreign ? yesOrNothing(a.fx_override) : null },
    { label: "From account", value: psFrom ? psLabel(psFrom, psOwnerName(psFrom, students, parties)) : null },
    { label: "To account", value: bankTo ? (bankTo.label || psLabel(bankTo, psOwnerName(bankTo, students, parties))) : null },
    { label: "Repayment currency", value: sameCurrency(repayCur, curCode(a.currency, "USD")) ? null : repayCur },
    { label: "Note", value: a.note },
    // NEW — the progress bar's two figures, in words
    { label: "Repaid so far", value: num(a.repaid_total) !== null ? inCurrency(a.repaid_total, a.currency) : null },
    isOverpaid(a.remaining)
      ? { label: "Overpaid by", value: inCurrency(-a.remaining, a.currency) + (a.remaining_is_estimate ? " (estimate)" : "") }
      : { label: "Still owed", value: num(a.remaining) !== null ? inCurrency(a.remaining, a.currency) + (a.remaining_is_estimate ? " (estimate)" : "") : null },
    { heading: "Cash advance reimbursement" },
    { label: "Date", value: a.reimb_date ? fmtDate(a.reimb_date) : null },
    { label: "Amount", value: num(a.reimb_amount) !== null ? inCurrency(a.reimb_amount, a.reimb_currency || repayCur) : null },
    { label: "From account", value: anyAccount(a.reimb_from_account) },
    { label: "To account", value: anyAccount(a.reimb_to_account) },
    { label: "Note", value: a.reimb_note },
  ];
}

// ── FAMILY MONEY / CONTRIBUTION ENVELOPE ───────────────────────────────────
//
// ctx: { studentName, reimbursementLabel: (key) => string }
export function envelopeFields(e, ctx = {}) {
  const { studentName, reimbursementLabel } = ctx;
  const foreign = e.currency && !isUSD(e.currency);
  const source = e.fx_override || e.fx_source === "manual" ? "manual" : e.fx_source;
  return [
    { label: "Beneficiary", value: nameOrNothing(studentName) },
    { label: "Paid by", value: e.giver_nickname },
    { label: "Date", value: e.date ? fmtDate(e.date) : null },
    { label: "Amount", value: inCurrency(e.amount, e.currency) },
    { label: "US dollar amount", value: foreign ? dollars(e.usd_amount) : null },
    { label: "Exchange rate", value: foreign ? fxLine(e.currency, e.fx_rate, source, e.fx_date) : null },
    { label: "Rate entered by hand", value: foreign ? yesOrNothing(e.fx_override) : null },
    { label: "Reimbursement", value: reimbursementLabel ? reimbursementLabel(e.reimbursement) || null : null },
    { label: "Deferred details — when is reimbursement expected?", value: e.reimbursement === "deferred" ? e.deferred_note : null },
    { label: "Note", value: e.note || e.notes },
    // NEW — the card's two running figures
    { label: "Tagged so far", value: num(e.allocated_usd) !== null ? dollars(e.allocated_usd) : null },
    { label: "Remaining", value: num(e.remaining_usd) !== null ? dollars(e.remaining_usd) : null },
  ];
}

// ── GIFT ───────────────────────────────────────────────────────────────────
//
// ctx: { studentName }
export function giftFields(g, ctx = {}) {
  const { studentName } = ctx;
  const foreign = g.currency && !isUSD(g.currency);
  const legs = g.transfer_legs || g.transfer_hops || [];
  const source = g.fx_override ? "manual" : g.fx_source;
  return [
    { label: "Beneficiary", value: nameOrNothing(studentName) },
    { label: "Giver", value: g.giver_nickname },
    { label: "Date", value: g.date ? fmtDate(g.date) : null },
    { label: "Amount", value: inCurrency(g.amount, g.currency) },
    { label: "US dollar amount", value: foreign ? dollars(g.usd_amount) : null },
    { label: "Exchange rate", value: foreign ? fxLine(g.currency, g.fx_rate, source, g.fx_date) : null },
    { label: "Rate entered by hand", value: foreign ? yesOrNothing(g.fx_override) : null },
    { label: "Payment method", value: g.payment_method },
    { label: "Note", value: g.note },
    { heading: "Transfer legs" },
    ...legs.map((l, i) => ({
      label: `Leg ${i + 1}`,
      value: [
        [l.from, l.to].filter(Boolean).join(" → ") || null,
        l.date ? fmtDate(l.date) : null,
        num(l.amount) !== null ? inCurrency(l.amount, l.currency || g.currency) : null,
      ].filter(Boolean).join(" · ") || null,
    })),
  ];
}

// ── BENEFICIARY ────────────────────────────────────────────────────────────
//
// THE TAX-CREDIT ELECTION IS NOT HERE. The card fetches it from its own door
// when it draws the AOTC control, and the setup form fetches it again when it
// opens; the beneficiary record the list holds does not carry it. Same rule.
export function beneficiaryFields(s) {
  const out = [
    { label: "Full name", value: s.full_name },
    { label: "Nickname (shown everywhere)", value: s.nickname },
    { label: "Login", value: s.login_email },
    { label: "Show 529 balance", value: yesNo(s.show_529_balance !== false) },
    { label: "Archived", value: yesOrNothing(s.archived) },
  ];
  for (const sc of s.schools || []) {
    const higherEd = (sc.expense_group || "post_secondary") === "post_secondary";
    // ZEN-783: the school name, then one summary line (Maryse, 17 Oct 2026).
    const start = sc.start_date ? fmtDate(sc.start_date) : null;
    const end = sc.end_date ? fmtDate(sc.end_date) : null;
    const span = start && end ? `${start} to ${end} (expected completion)`
      : start ? `From ${start}` : end ? `Expected completion ${end}` : null;
    out.push({
      heading: sc.school_name || "Education period",
      detail: [groupLabel(sc.expense_group), sc.archived ? "Archived" : "Active", sc.currency, span].filter(Boolean).join(" · ") || null,
    });
    out.push({ label: "529 school code", value: higherEd ? sc.school_529_code : null });
    out.push({ label: "Country", value: sc.country });
    out.push({ label: "Student ID at this school", value: sc.student_school_id });
    out.push({ label: "Notes", value: sc.notes });
    for (const y of sc.coa_by_year || []) out.push(termTable(y, sc));
  }
  return out;
}

// ZEN-783: one academic year = one heading and one row per term (Maryse,
// 17 Oct 2026). A column says "per month" or "for the term" in its heading
// when every term agrees, and on each amount when they do not.
function termTable(y, sc) {
  const periods = [...(y.periods || [])].sort((a, b) => (a.from_month || "").localeCompare(b.from_month || ""));
  const perOf = (p) => (p.basis === "period" ? "for the term" : "per month");
  const pers = new Set(periods.map(perOf));
  const shared = pers.size === 1 ? [...pers][0] : null;
  const amount = (v, cur, p) => (num(v) === null ? null : `${inCurrency(v, cur)}${shared ? "" : ` ${perOf(p)}`}`);
  const rows = periods.map((p) => {
    const cur = curCode(p.coa_currency || sc.currency, "USD");
    const combined = (p.mode || "separate") === "combined";
    const term = termMonthsLong(p.from_month, p.to_month) || p.term_label || "Term";
    return {
      cells: [
        p.term_label && term !== p.term_label ? `${term} (${p.term_label})` : term,
        p.situation === "other" && p.situation_custom ? `Other: ${p.situation_custom}` : nameOf(HOUSING_SITUATION_LABEL, p.situation, p.situation_custom || null),
        ...(combined
          ? [{ text: num(p.combined) !== null ? `Room and board combined: ${amount(p.combined, cur, p)}` : null, span: 2 }]
          : [amount(p.food, cur, p), amount(p.housing, cur, p)]),
      ],
      detail: [
        num(p.school_invoiced_amount) !== null ? `School invoiced ${inCurrency(p.school_invoiced_amount, cur)}` : null,
        p.half_time === false ? "Less than half-time" : null,
        p.note || null,
      ].filter(Boolean).join(" · ") || null,
    };
  });
  return {
    table: {
      title: academicYearLabel(y.periods) || y.label || (y.year != null ? String(y.year) : "Academic year"),
      columns: ["Term", "Living", `Food${shared ? ` ${shared}` : ""}`, `Housing${shared ? ` ${shared}` : ""}`],
      rows,
    },
  };
}

// ── BANK ACCOUNT OR CARD ───────────────────────────────────────────────────
//
// ctx: { label, ownerName, studentName }
export function accountFields(a, ctx = {}) {
  const { label, ownerName, studentName } = ctx;
  return [
    { label: "Account label", value: label },
    { label: "Account owner", value: a.owner_type === "student" ? (nameOrNothing(studentName) || "Beneficiary") : (ownerName ? `Owner (${ownerName})` : "Owner") },
    { label: "Institution · short code", value: a.institution },
    { label: "Type", value: nameOf(ACCOUNT_TYPE_LABEL, a.type, UNKNOWN_ACCOUNT_TYPE) },
    { label: "Last 4 digits", value: a.last4 },
    { label: "Currency", value: a.currency },
    { label: "Notes", value: a.notes },
    { label: "Default account for new expenses", value: yesOrNothing(a.is_default) },
    { label: "Archived", value: yesOrNothing(a.archived) },
  ];
}

// ── PAYER (A PERSON) ───────────────────────────────────────────────────────
export function payerFields(p) {
  const n = p.ref_count ?? null;
  return [
    { label: "Nickname", value: p.nickname },
    { label: "Full name", value: p.full_name },
    { label: "Relationship", value: p.role && p.role !== "other" ? p.role : null },
    { label: "Email", value: p.email },
    { label: "Notes", value: p.notes },
    { label: "Used in", value: n === null ? null : `${n} record${n === 1 ? "" : "s"}` },
  ];
}

// ── 529 PLAN ───────────────────────────────────────────────────────────────
//
// ctx: { label, studentName }
export function planFields(fa, ctx = {}) {
  const { label, studentName } = ctx;
  return [
    { label: "Account label", value: label },
    { label: "Beneficiary", value: nameOrNothing(studentName) },
    { label: "Provider / plan name", value: fa.provider },
    { label: "Plan home state", value: fa.state ? stateName(fa.state) : null },
    { label: "Last 4 digits", value: fundLast4(fa) || null },
    { label: "Notes", value: fa.notes },
    { label: "Archived", value: yesOrNothing(fa.archived) },
  ];
}

// ── SCHOLARSHIP / AID ──────────────────────────────────────────────────────
//
// ctx: { studentName, sourceLabel }
export function scholarshipFields(s, ctx = {}) {
  const { studentName, sourceLabel } = ctx;
  return [
    { label: "Label", value: s.label },
    { label: "Beneficiary", value: nameOrNothing(studentName) },
    { label: "Tax year", value: s.tax_year || null },
    { label: "Source type", value: sourceLabel || s.source_type },
    { label: "Amount (USD)", value: dollars(s.amount) },
    { label: "Date received", value: s.date_received ? fmtDate(s.date_received) : null },
    { label: "Tax-free (reduces AQEE)", value: yesNo(s.tax_free !== false) },
    { label: "Notes", value: s.notes },
  ];
}

// ── SETTLEMENT (ONE LINE OF THE SETTLEMENT HISTORY) ────────────────────────
//
// ctx: { between: "Reimburser → Payor" }
export function settlementFields(s, ctx = {}) {
  const { between } = ctx;
  const foreign = s.currency && !isUSD(s.currency);
  const linked = (s.linked_expense_ids || []).length;
  return [
    { label: "Date", value: s.date ? fmtDate(s.date) : null },
    { label: "Amount", value: foreign ? inCurrency(s.amount, s.currency) : dollars(s.amount_usd ?? s.amount) },
    { label: "US dollar amount", value: foreign ? dollars(s.amount_usd) : null },
    { label: "Method", value: s.method || s.reimbursement_method },
    { label: "From account", value: s.from_account },
    { label: "To account", value: s.to_account },
    { label: "Note", value: s.note || s.notes },
    // NEW — who paid whom, as the history heading already words it
    { label: "Between", value: between || null },
    { label: "Linked expenses", value: linked ? String(linked) : null },
  ];
}
